// src/dashboard/pages/teacher/ToDoList/hooks/useToDoListDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllTasks,
  addTask,
  updateTask,
  deleteTask,
} from "../../../../../services/tachesServices";

import { getAllUsers } from "../../../../../services/userServices";
import { sendNotification } from "../../../../../services/notificationServices";

import {
  buildTaskPayload,
  calculateTaskStats,
  filterTasks,
  getCategoryOptions,
  groupTasksByStatus,
  isQuillEmpty,
  sortUsers,
  STATUS_ORDER,
} from "../utils/toDoListDashboardUtils";

const defaultFilters = {
  query: "",
  status: "",
  userId: "",
  category: "",
};

export function useToDoListDashboard(currentUser) {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [dragOverStatus, setDragOverStatus] = useState("");

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [tasksResponse, usersResponse] = await Promise.all([
        getAllTasks(),
        getAllUsers(),
      ]);

      const taskRows = Array.isArray(tasksResponse) ? tasksResponse : [];

      const userRows = (Array.isArray(usersResponse) ? usersResponse : [])
        .filter((user) => user.role_id === 1 || user.role_id === 3)
        .sort(sortUsers);

      setTasks(taskRows);
      setUsers(userRows);
    } catch (error) {
      console.error("Erreur chargement ToDoList :", error);
      toast.error("Erreur lors du chargement des tâches.");
      setTasks([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const categoryOptions = useMemo(() => {
    return getCategoryOptions(tasks);
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return filterTasks(tasks, filters);
  }, [tasks, filters]);

  const groupedTasks = useMemo(() => {
    return groupTasksByStatus(filteredTasks);
  }, [filteredTasks]);

  const stats = useMemo(() => {
    return calculateTaskStats(tasks, filteredTasks);
  }, [tasks, filteredTasks]);

  const openAddDrawer = () => {
    setAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    if (saving) return;
    setAddDrawerOpen(false);
  };

  const openEditDrawer = (task) => {
    setCurrentTask(task);
    setEditDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    if (saving) return;
    setCurrentTask(null);
    setEditDrawerOpen(false);
  };

  const handleCreateTask = async (formData) => {
    if (!formData.categorie?.trim()) {
      toast.warn("La catégorie est requise.");
      return;
    }

    if (!formData.titre?.trim()) {
      toast.warn("Le titre est requis.");
      return;
    }

    if (isQuillEmpty(formData.contenu)) {
      toast.warn("La description est requise.");
      return;
    }

    if (!formData.user_id) {
      toast.warn("Sélectionnez un utilisateur.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildTaskPayload(formData);

      await addTask(payload);

      try {
        const senderName = `${currentUser?.nom || ""} ${
          currentUser?.prenom || ""
        }`.trim();

        const message = `${
          senderName || "Un responsable"
        } vous a attribué une nouvelle tâche : ${payload.titre}`;

        await sendNotification(message, payload.user_id, currentUser?.token);
      } catch (notificationError) {
        console.warn("Notification non envoyée :", notificationError);
      }

      toast.success("Tâche ajoutée avec succès.");
      setAddDrawerOpen(false);

      await fetchDashboardData();
    } catch (error) {
      console.error("Erreur création tâche :", error);
      toast.error("Erreur lors de la création de la tâche.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTask = async (id, formData) => {
    if (!id) {
      toast.error("Tâche introuvable.");
      return;
    }

    if (!formData.categorie?.trim()) {
      toast.warn("La catégorie est requise.");
      return;
    }

    if (!formData.titre?.trim()) {
      toast.warn("Le titre est requis.");
      return;
    }

    if (isQuillEmpty(formData.contenu)) {
      toast.warn("La description est requise.");
      return;
    }

    if (!formData.user_id) {
      toast.warn("Sélectionnez un utilisateur.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildTaskPayload(formData);

      await updateTask(id, payload);

      toast.success("Tâche mise à jour.");
      setEditDrawerOpen(false);
      setCurrentTask(null);

      await fetchDashboardData();
    } catch (error) {
      console.error("Erreur mise à jour tâche :", error);
      toast.error("Erreur lors de la mise à jour de la tâche.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (taskOrId) => {
    const id = typeof taskOrId === "object" ? taskOrId?.id : taskOrId;

    if (!id) {
      toast.error("Tâche introuvable.");
      return;
    }

    if (!window.confirm("Supprimer cette tâche ?")) {
      return;
    }

    try {
      setDeletingId(id);

      await deleteTask(id);

      toast.success("Tâche supprimée.");

      setTasks((prev) => prev.filter((task) => task.id !== id));

      if (currentTask?.id === id) {
        setCurrentTask(null);
        setEditDrawerOpen(false);
      }
    } catch (error) {
      console.error("Erreur suppression tâche :", error);
      toast.error("Erreur lors de la suppression de la tâche.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleChangeTaskStatus = async (task, newStatus) => {
    if (!task?.id || !newStatus || task.statut === newStatus) return;

    const previousTasks = tasks;

    setTasks((current) =>
      current.map((item) =>
        item.id === task.id ? { ...item, statut: newStatus } : item
      )
    );

    try {
      await updateTask(task.id, { statut: newStatus });
      toast.success(`Statut mis à jour : ${newStatus}`);
    } catch (error) {
      console.error("Erreur changement statut :", error);
      setTasks(previousTasks);
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  const handleDragStart = (event, task) => {
    event.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        id: task.id,
        from: task.statut,
      })
    );

    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event, status) => {
    event.preventDefault();

    if (dragOverStatus !== status) {
      setDragOverStatus(status);
    }
  };

  const handleDragEnter = (event, status) => {
    event.preventDefault();
    setDragOverStatus(status);
  };

  const handleDragLeave = (_event, status) => {
    if (dragOverStatus === status) {
      setDragOverStatus("");
    }
  };

  const handleDrop = async (event, toStatus) => {
    event.preventDefault();
    setDragOverStatus("");

    try {
      const raw = event.dataTransfer.getData("text/plain");
      const data = JSON.parse(raw || "{}");

      if (!data?.id) return;

      const task = tasks.find((item) => String(item.id) === String(data.id));

      if (!task || task.statut === toStatus) return;

      await handleChangeTaskStatus(task, toStatus);
    } catch (error) {
      console.error("Erreur drop tâche :", error);
    }
  };

  const refreshTasks = useCallback(async () => {
    await fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    tasks,
    filteredTasks,
    groupedTasks,
    users,
    categoryOptions,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,
    dragOverStatus,

    addDrawerOpen,
    editDrawerOpen,
    currentTask,

    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,

    handleCreateTask,
    handleUpdateTask,
    handleDeleteTask,
    handleChangeTaskStatus,

    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,

    refreshTasks,
  };
}